import React from 'react';
import DatePicker from 'react-datepicker';
import ReactDOM from 'react-dom';
import moment from 'moment';
import CustomSelectInput from './CustomSelectInput';
import CSVExport, { Header } from './CSVExport';
import { Column } from './InlineEditTable/types';
import { formatPhoneNumber } from '../../utils/number-util';

export type SelectOption = { value: string; label: string };

type BaseItem = {
  key: string;
  label: React.ReactNode;
  placeholder?: string;
  minWidth?: number | string;
  maxWidth?: number | string;
  flex?: string | number;
  span?: number; // Bootstrap-like 12-col grid span
};

type SearchItem = BaseItem & {
  type: 'search';
};

type SelectItem = BaseItem & {
  type: 'select';
  options: SelectOption[];
  multi?: boolean;
  styles?: any;
  valueMapper?: (values: Array<string | number>) => SelectOption[];
  onChangeMapper?: (value: any) => Record<string, any>;
};

type DateItem = BaseItem & {
  type: 'date';
  popperWidth?: number;
};

type NumberItem = BaseItem & {
  type: 'number';
  min?: number;
  max?: number;
  step?: number;
};

type DateRangeItem = BaseItem & {
  type: 'dateRange';
  startKey: string;
  endKey: string;
  startPlaceholder?: string;
  endPlaceholder?: string;
};

type NumberRangeItem = BaseItem & {
  type: 'numberRange';
  startKey: string;
  endKey: string;
  startPlaceholder?: string;
  endPlaceholder?: string;
  min?: number;
  max?: number;
  step?: number;
};

export type FilterItem =
  | SearchItem
  | SelectItem
  | DateItem
  | NumberItem
  | DateRangeItem
  | NumberRangeItem;

export interface GenericFilterBarProps {
  intl: any;
  items: FilterItem[];
  values: Record<string, any>;
  onChange: (patch: Record<string, any>) => void;
  style?: React.CSSProperties;
  collapsible?: boolean;
  deferred?: boolean;
  onApply?: (values: Record<string, any>) => void;
  onReset?: () => void;
  useHeaderActions?: boolean;
  headerActions?: React.ReactNode;
  footerContent?: React.ReactNode;
  canExport?: boolean;
  exportHeaders?: Column[];
  exportData?: Array<Record<string, any>>;
  extraContent?: React.ReactNode;
}

const GenericFilterBar: React.FC<GenericFilterBarProps> = ({
  intl,
  items = [],
  values = {},
  onChange,
  style = {},
  collapsible = true,
  deferred = true,
  onApply,
  onReset,
  useHeaderActions = true,
  headerActions,
  footerContent,
  canExport = false,
  exportHeaders = [],
  exportData = [],
  extraContent,
}) => {
  const [isCollapsed, setIsCollapsed] = React.useState(collapsible);
  const [localValues, setLocalValues] = React.useState(values);
  const formattedHeaders: Header[] = exportHeaders.map((header) => ({
    name: header.title,
    key: header.keyName ?? '',
    databaseProperty: '',
  }));
  const formattedData: any[] = exportData.map((data) => ({
    ...data,
    customerPhone: formatPhoneNumber(data.customerPhone),
    _costRaw: new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: data?._costCurrency ?? 'USD',
    }).format(data?.cost),
  }));

  React.useEffect(() => {
    if (!deferred) {
      setLocalValues(values);
    }
  }, [values, deferred]);

  const activeFilterCount = React.useMemo(() => {
    return Object.entries(localValues).filter(([key, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'string') return value !== '';
      return value != null && value !== '';
    }).length;
  }, [localValues]);
  const set = (patch: Record<string, any>) => {
    const newValues = { ...localValues, ...patch };
    setLocalValues(newValues);
    if (!deferred) {
      onChange(newValues);
    }
  };

  const handleApply = () => {
    if (onApply) {
      onApply(localValues);
    } else {
      onChange(localValues);
    }
  };

  const handleReset = () => {
    const resetValues = Object.keys(localValues).reduce((acc, key) => {
      acc[key] = Array.isArray(localValues[key]) ? [] : '';
      return acc;
    }, {} as Record<string, any>);
    setLocalValues(resetValues);
    if (onReset) {
      onReset();
    } else if (!deferred) {
      onChange(resetValues);
    }
  };
  const gapPx = 16;

  const renderItem = (item: FilterItem) => {
    const { key, type, label, placeholder, minWidth = 140, maxWidth = 200 } = item as any;
    const commonWrapperStyle: React.CSSProperties = {
      minWidth: minWidth as any,
      maxWidth,
      display: 'flex',
      flexDirection: 'column',
      flex: `1 1 ${minWidth}px`,
      justifyContent: 'flex-end',
    };
    const labelStyle: React.CSSProperties = {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    };
    const clearButtonStyle: React.CSSProperties = {
      position: 'absolute',
      right: 8,
      top: '50%',
      transform: 'translateY(-50%)',
      padding: 2,
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#cccccc',
      fontSize: 20,
      lineHeight: 1,
      width: 20,
      height: 20,
    };

    if (type === 'search') {
      return (
        <div key={key} style={commonWrapperStyle}>
          <div className="text-muted small mb-1" style={labelStyle}>
            {label}
          </div>
          <div style={{ position: 'relative', width: '100%' }}>
            <input
              className="form-control"
              placeholder={placeholder}
              value={localValues[key] || ''}
              onChange={(e) => set({ [key]: (e.target as HTMLInputElement).value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleApply();
              }}
            />
            {localValues[key] ? (
              <button
                type="button"
                onClick={() => set({ [key]: '' })}
                aria-label={intl.formatMessage({ id: 'clear', defaultMessage: 'Clear' })}
                style={clearButtonStyle}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#999999')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#cccccc')}
              >
                ×
              </button>
            ) : (
              <i
                className="uil uil-search"
                style={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#94a3b8',
                  pointerEvents: 'none',
                  fontSize: 18,
                }}
              />
            )}
          </div>
        </div>
      );
    }

    if (type === 'number') {
      const { min, max, step = 1 } = item as NumberItem;
      return (
        <div key={key} style={commonWrapperStyle}>
          <div className="text-muted small mb-1" style={labelStyle}>
            {label}
          </div>
          <div style={{ position: 'relative', width: '100%' }}>
            <input
              className="form-control filterbar-number-input"
              type="number"
              min={min}
              max={max}
              step={step}
              placeholder={placeholder}
              value={localValues[key] ?? ''}
              onChange={(e) =>
                set({ [key]: e.currentTarget.value === '' ? '' : Number(e.currentTarget.value) })
              }
            />
            {localValues[key] !== '' && localValues[key] !== undefined ? (
              <button
                type="button"
                onClick={() => set({ [key]: '' })}
                aria-label={intl.formatMessage({ id: 'clear', defaultMessage: 'Clear' })}
                style={clearButtonStyle}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#999999')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#cccccc')}
              >
                ×
              </button>
            ) : null}
          </div>
        </div>
      );
    }

    if (type === 'select') {
      const {
        options = [],
        multi = false,
        styles,
        valueMapper,
        onChangeMapper,
      } = item as SelectItem;
      const raw = localValues[key];
      const currentValue: Array<string | number> = Array.isArray(raw) ? raw : raw ? [raw] : [];
      const mapValue = (arr: Array<string | number>): SelectOption[] => {
        if (typeof valueMapper === 'function') return valueMapper(arr);
        return (arr || []).map((v) => ({ value: String(v), label: String(v) }));
      };
      const handleChange = (val: any) => {
        if (!val || (Array.isArray(val) && !val.length)) {
          return set({ [key]: [] });
        }
        if (typeof onChangeMapper === 'function') {
          return set(onChangeMapper(val));
        }
        const value = (Array.isArray(val) ? val : [val]).map((o) => o.value).flat(Infinity);
        return set({
          [key]: value.filter((keyVal, index) => value.indexOf(keyVal) === index),
        });
      };
      const defaultSelectStyles: any = {
        container: { width: '100%', minWidth: 0, flex: 1 },
        menu: { width: 260, zIndex: 2000 },
        valueContainer: { overflow: 'hidden', minWidth: 0 },
        singleValue: {
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      };
      const mergedStyles = styles ? { ...defaultSelectStyles, ...styles } : defaultSelectStyles;
      return (
        <div key={key} style={commonWrapperStyle}>
          <div className="text-muted small mb-1" style={labelStyle}>
            {label}
          </div>
          <CustomSelectInput
            multi={multi}
            styles={mergedStyles}
            options={options}
            value={mapValue(currentValue)}
            onChange={handleChange}
            placeholder={placeholder || ''}
            isClearable
          />
        </div>
      );
    }

    if (type === 'date') {
      const { popperWidth = 200 } = item as DateItem;
      const val = localValues[key];
      return (
        <div key={key} style={commonWrapperStyle} className="qt-date-filter">
          <div className="text-muted small mb-1" style={labelStyle}>
            {label}
          </div>
          <div style={{ position: 'relative', width: '100%' }}>
            <DatePicker
              selected={val ? moment(val).toDate() : null}
              onChange={(date: Date | null) =>
                set({ [key]: date ? moment(date).toISOString() : '' })
              }
              placeholderText={placeholder || ''}
              className="form-control"
              wrapperClassName="w-100"
              calendarClassName="qt-datepicker"
              popperPlacement="bottom-start"
              style={{ width: '100%' }}
              dateFormat="MM/dd/yyyy"
              isClearable={false}
              popperClassName="qt-datepicker-popper"
              shouldCloseOnScroll={false}
              popperModifiers={[]}
              popperContainer={({ children }: { children: React.ReactNode }) =>
                ReactDOM.createPortal(
                  <div
                    className="qt-datepicker-portal"
                    style={{ zIndex: 2000, minWidth: popperWidth }}
                  >
                    {children}
                  </div>,
                  document.body
                )
              }
            />
            {val ? (
              <button
                type="button"
                onClick={() => set({ [key]: '' })}
                aria-label={intl.formatMessage({ id: 'clearDate', defaultMessage: 'Clear date' })}
                style={clearButtonStyle}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#999999')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#cccccc')}
              >
                ×
              </button>
            ) : (
              <div
                style={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  pointerEvents: 'none',
                }}
              >
                <i
                  className="uil uil-calender"
                  style={{
                    color: '#6b7280',
                    fontSize: 24,
                    lineHeight: 1,
                  }}
                />
              </div>
            )}
          </div>
        </div>
      );
    }

    if (type === 'dateRange') {
      const { startKey, endKey, startPlaceholder, endPlaceholder } = item as DateRangeItem;
      const startVal = localValues[startKey];
      const endVal = localValues[endKey];
      return (
        <div key={key} style={{ ...commonWrapperStyle, minWidth: 300, maxWidth: 400 }}>
          <div className="text-muted small mb-1" style={labelStyle}>
            {label}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <DatePicker
                selected={startVal ? moment(startVal).toDate() : null}
                onChange={(date: Date | null) =>
                  set({ [startKey]: date ? moment(date).toISOString() : '' })
                }
                placeholderText={
                  startPlaceholder ||
                  intl.formatMessage({ id: 'filterStart', defaultMessage: 'Start' })
                }
                className="form-control"
                wrapperClassName="w-100"
                calendarClassName="qt-datepicker"
                popperPlacement="bottom-start"
                dateFormat="MM/dd/yyyy"
                isClearable={false}
                popperClassName="qt-datepicker-popper"
                shouldCloseOnScroll={false}
                popperModifiers={[]}
                popperContainer={({ children }: { children: React.ReactNode }) =>
                  ReactDOM.createPortal(
                    <div className="qt-datepicker-portal" style={{ zIndex: 2000, minWidth: 200 }}>
                      {children}
                    </div>,
                    document.body
                  )
                }
              />
              {startVal ? (
                <button
                  type="button"
                  onClick={() => set({ [startKey]: '' })}
                  aria-label={intl.formatMessage({ id: 'clearDate', defaultMessage: 'Clear date' })}
                  style={clearButtonStyle}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#999999')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#cccccc')}
                >
                  ×
                </button>
              ) : (
                <div
                  style={{
                    position: 'absolute',
                    right: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    pointerEvents: 'none',
                  }}
                >
                  <i
                    className="uil uil-calender"
                    style={{
                      color: '#6b7280',
                      fontSize: 24,
                      lineHeight: 1,
                    }}
                  />
                </div>
              )}
            </div>
            <span style={{ color: '#6b7280', fontWeight: 500 }}>—</span>
            <div style={{ position: 'relative', flex: 1 }}>
              <DatePicker
                selected={endVal ? moment(endVal).toDate() : null}
                onChange={(date: Date | null) =>
                  set({ [endKey]: date ? moment(date).toISOString() : '' })
                }
                placeholderText={
                  endPlaceholder || intl.formatMessage({ id: 'filterEnd', defaultMessage: 'End' })
                }
                className="form-control"
                wrapperClassName="w-100"
                calendarClassName="qt-datepicker"
                popperPlacement="bottom-start"
                dateFormat="MM/dd/yyyy"
                isClearable={false}
                popperClassName="qt-datepicker-popper"
                shouldCloseOnScroll={false}
                popperModifiers={[]}
                popperContainer={({ children }: { children: React.ReactNode }) =>
                  ReactDOM.createPortal(
                    <div className="qt-datepicker-portal" style={{ zIndex: 2000, minWidth: 200 }}>
                      {children}
                    </div>,
                    document.body
                  )
                }
              />
              {endVal ? (
                <button
                  type="button"
                  onClick={() => set({ [endKey]: '' })}
                  aria-label={intl.formatMessage({ id: 'clearDate', defaultMessage: 'Clear date' })}
                  style={clearButtonStyle}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#999999')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#cccccc')}
                >
                  ×
                </button>
              ) : (
                <div
                  style={{
                    position: 'absolute',
                    right: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    pointerEvents: 'none',
                  }}
                >
                  <i
                    className="uil uil-calender"
                    style={{
                      color: '#6b7280',
                      fontSize: 24,
                      lineHeight: 1,
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (type === 'numberRange') {
      const {
        startKey,
        endKey,
        startPlaceholder,
        endPlaceholder,
        min,
        max,
        step = 1,
      } = item as NumberRangeItem;
      const startVal = localValues[startKey];
      const endVal = localValues[endKey];
      return (
        <div key={key} style={{ ...commonWrapperStyle, minWidth: 180, maxWidth: 220 }}>
          <div className="text-muted small mb-1" style={labelStyle}>
            {label}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <input
                className="form-control filterbar-number-input"
                type="number"
                min={min}
                max={max}
                step={step}
                placeholder={startPlaceholder || '0'}
                value={startVal ?? ''}
                onChange={(e) =>
                  set({
                    [startKey]: e.currentTarget.value === '' ? '' : Number(e.currentTarget.value),
                  })
                }
              />
              {startVal !== '' && startVal !== undefined ? (
                <button
                  type="button"
                  onClick={() => set({ [startKey]: '' })}
                  aria-label="Clear"
                  style={clearButtonStyle}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#999999')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#cccccc')}
                >
                  ×
                </button>
              ) : null}
            </div>
            <span style={{ color: '#6b7280', fontWeight: 500 }}>—</span>
            <div style={{ position: 'relative', flex: 1 }}>
              <input
                className="form-control filterbar-number-input"
                type="number"
                min={min}
                max={max}
                step={step}
                placeholder={endPlaceholder || '100'}
                value={endVal ?? ''}
                onChange={(e) =>
                  set({
                    [endKey]: e.currentTarget.value === '' ? '' : Number(e.currentTarget.value),
                  })
                }
              />
              {endVal !== '' && endVal !== undefined ? (
                <button
                  type="button"
                  onClick={() => set({ [endKey]: '' })}
                  aria-label="Clear"
                  style={clearButtonStyle}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#999999')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#cccccc')}
                >
                  ×
                </button>
              ) : null}
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div>
      {useHeaderActions && (
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            justifyContent: 'space-between',
          }}
        >
          <div
            className={
              collapsible ? 'filter-bar-header filter-bar-header--clickable' : 'filter-bar-header'
            }
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              ...(collapsible && {
                cursor: 'pointer',
              }),
            }}
            {...(collapsible && {
              role: 'button',
              tabIndex: 0,
              onClick: () => setIsCollapsed(!isCollapsed),
              onKeyDown: (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsCollapsed((prev) => !prev);
                }
              },
              'aria-label': intl.formatMessage({
                id: isCollapsed ? 'expandFilters' : 'collapseFilters',
                defaultMessage: isCollapsed ? 'Expand filters' : 'Collapse filters',
              }),
            })}
          >
            <i className="uil uil-filter" />
            <span className="desktop-label" style={{ fontWeight: 'bold', fontSize: 16 }}>
              {intl.formatMessage(
                { id: 'filters', defaultMessage: 'Filters{count}' },
                { count: activeFilterCount > 0 ? ` (${activeFilterCount})` : '' }
              )}
            </span>
            {collapsible && (
              <i
                className={`uil uil-angle-${isCollapsed ? 'down' : 'up'}`}
                style={{ fontSize: 20 }}
              />
            )}
          </div>
          {headerActions}
        </div>
      )}
      {!isCollapsed && (
        <div className="card mb-3 filter-bar-container" style={{ marginTop: 0, ...style }}>
          <div className="card-body" style={{ padding: '1rem' }}>
            <div
              className="align-items-end"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: gapPx,
                overflowX: 'visible',
                alignItems: 'flex-end',
              }}
            >
              {items.map((it) => renderItem(it))}
              {extraContent && <div style={{ flex: '0 0 auto' }}>{extraContent}</div>}
            </div>
            {footerContent && <div style={{ marginTop: '0.75rem' }}>{footerContent}</div>}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
                marginTop: '0.5rem',
              }}
            >
              <button
                type="button"
                onClick={handleReset}
                className="button"
                style={{ fontSize: 13 }}
              >
                {intl.formatMessage({ id: 'reset', defaultMessage: 'Reset' })}
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="button cancel"
                style={{ fontSize: 13, color: 'white' }}
              >
                {intl.formatMessage(
                  { id: 'applyFilters', defaultMessage: 'Apply Filters{count}' },
                  { count: activeFilterCount > 0 ? ` (${activeFilterCount})` : '' }
                )}
              </button>
              {canExport && (
                <CSVExport data={formattedData} headers={formattedHeaders} noMargin={true} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenericFilterBar;
